import React from 'react';
import PropTypes from 'prop-types';
import secureLog from '../../util/secureLogger';

/**
 * Error Boundary component to catch JavaScript errors anywhere in the child component tree,
 * log those errors securely, and display a fallback UI instead of crashing the whole app.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error securely
    secureLog.error('Component error caught by boundary', error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary" style={styles.container}>
          <h2 style={styles.heading}>Something went wrong</h2>
          <p style={styles.message}>
            We're sorry, but there was an error loading this page. Please try refreshing or contact support if the problem persists.
          </p>
          {process.env.NODE_ENV === 'development' && (
            <details style={styles.details}>
              <summary style={styles.summary}>Developer Details</summary>
              <pre style={styles.pre}>{this.state.errorInfo?.componentStack || 'No stack trace available'}</pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

// Inline styles for the error UI
const styles = {
  container: {
    padding: '20px',
    margin: '20px',
    borderRadius: '5px',
    backgroundColor: '#f8d7da',
    color: '#721c24',
    border: '1px solid #f5c6cb',
    fontFamily: 'sans-serif'
  },
  heading: {
    marginTop: '0',
    color: '#721c24'
  },
  message: {
    fontSize: '16px',
    lineHeight: '1.5'
  },
  details: {
    marginTop: '20px',
    padding: '10px',
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    borderRadius: '4px'
  },
  summary: {
    fontWeight: 'bold',
    cursor: 'pointer'
  },
  pre: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: '300px',
    overflow: 'auto',
    padding: '10px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    fontSize: '12px'
  }
};

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

export default ErrorBoundary; 